import { useEffect, useState } from "react";
import { Button, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { Chat24Regular, Dismiss24Regular, Subtract24Regular, ArrowClockwise24Regular } from "@fluentui/react-icons";
import { Components } from "botframework-webchat";
import { FluentThemeProvider } from "botframework-webchat-fluent-theme";
import {
  CopilotStudioClient,
  ConnectionSettings,
  CopilotStudioWebChat,
  type CopilotStudioWebChatConnection,
} from "@microsoft/agents-copilotstudio-client";
import { LeaveSystemConfig } from "../config/leaveSystemConfig";
import { acquireTokenForScope } from "../services/authClient";

const { BasicWebChat, Composer } = Components;

// Prod Power Platform scope; scopeFromSettings() overrides it when available
// (it handles sovereign clouds). We're on the public cloud, so this is the value.
const FALLBACK_SCOPE = "https://api.powerplatform.com/CopilotStudio.Copilots.Invoke";

const useStyles = makeStyles({
  launcher: {
    position: "fixed",
    right: tokens.spacingHorizontalXL,
    bottom: tokens.spacingVerticalXL,
    zIndex: 1000,
    minWidth: "56px",
    height: "56px",
    borderRadius: "50%",
    boxShadow: tokens.shadow16,
  },
  panel: {
    position: "fixed",
    right: tokens.spacingHorizontalXL,
    bottom: tokens.spacingVerticalXL,
    zIndex: 1000,
    width: "380px",
    maxWidth: `calc(100vw - ${tokens.spacingHorizontalXL} * 2)`,
    height: "560px",
    maxHeight: "calc(100vh - 96px)",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow28,
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: "#0A2A4A",
  },
  headerTitle: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  headerText: { color: tokens.colorNeutralForegroundOnBrand },
  headerIcon: { color: tokens.colorNeutralForegroundOnBrand, display: "inline-flex" },
  headerActions: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXXS },
  headerButton: { color: tokens.colorNeutralForegroundOnBrand },
  body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", backgroundColor: tokens.colorNeutralBackground1 },
  webchat: { flex: 1, minHeight: 0 },
  center: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalXL,
    textAlign: "center",
  },
  errorText: { color: tokens.colorNeutralForeground3 },
});

type Status = "idle" | "connecting" | "ready" | "error";

/**
 * Floating "Ask Katie" chat bubble. Connects to the Copilot Studio agent via
 * the Microsoft 365 Agents SDK, authenticating with the app's existing MSAL
 * sign-in so Katie answers as the signed-in user (and can read the SharePoint
 * leave lists). Renders nothing unless MSAL + agent config are present.
 */
export function AskKatie() {
  const s = useStyles();
  const [open, setOpen] = useState(false);
  const [connection, setConnection] = useState<CopilotStudioWebChatConnection | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  const { clientId, tenantId, katie } = LeaveSystemConfig;
  const enabled = Boolean(clientId && tenantId && katie.environmentId && katie.schemaName);

  useEffect(() => {
    if (!open || !enabled || connection || status === "connecting") return;
    let cancelled = false;
    (async () => {
      setStatus("connecting");
      setError("");
      try {
        const settings = new ConnectionSettings({
          environmentId: katie.environmentId,
          schemaName: katie.schemaName,
        });
        settings.appClientId = clientId;
        settings.tenantId = tenantId;
        settings.authority = "https://login.microsoftonline.com";

        const scopeFn = (CopilotStudioClient as unknown as {
          scopeFromSettings?: (s: ConnectionSettings) => string;
        }).scopeFromSettings;
        const scope = (typeof scopeFn === "function" && scopeFn(settings)) || FALLBACK_SCOPE;

        const token = await acquireTokenForScope(clientId, tenantId, scope);
        const client = new CopilotStudioClient(settings, token);
        const conn = CopilotStudioWebChat.createConnection(client, { showTyping: true });
        if (!cancelled) {
          setConnection(conn);
          setStatus("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error)?.message ?? String(e));
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // retry is a dep so the "Try again" button re-runs the connect.
  }, [open, enabled, connection, status, clientId, tenantId, katie.environmentId, katie.schemaName, retry]);

  if (!enabled) return null;

  if (!open) {
    return (
      <Button
        className={s.launcher}
        appearance="primary"
        shape="circular"
        icon={<Chat24Regular />}
        onClick={() => setOpen(true)}
        aria-label="Ask Katie"
        title="Ask Katie"
      />
    );
  }

  const retryConnect = () => {
    setConnection(null);
    setStatus("idle");
    setRetry((n) => n + 1);
  };

  return (
    <div className={s.panel} role="dialog" aria-label="Ask Katie chat">
      <div className={s.header}>
        <div className={s.headerTitle}>
          <span className={s.headerIcon}>
            <Chat24Regular />
          </span>
          <Text weight="semibold" className={s.headerText}>
            Ask Katie
          </Text>
        </div>
        <div className={s.headerActions}>
          <Button
            className={s.headerButton}
            appearance="transparent"
            icon={<Subtract24Regular />}
            onClick={() => setOpen(false)}
            aria-label="Minimize"
            title="Minimize"
          />
          <Button
            className={s.headerButton}
            appearance="transparent"
            icon={<Dismiss24Regular />}
            onClick={() => setOpen(false)}
            aria-label="Close"
            title="Close"
          />
        </div>
      </div>

      <div className={s.body}>
        {status === "ready" && connection ? (
          <div className={s.webchat}>
            <FluentThemeProvider>
              <Composer directLine={connection}>
                <BasicWebChat />
              </Composer>
            </FluentThemeProvider>
          </div>
        ) : status === "error" ? (
          <div className={s.center}>
            <Text weight="semibold">Couldn't connect to Katie</Text>
            <Text size={200} className={s.errorText}>
              {error || "Please try again."}
            </Text>
            <Button icon={<ArrowClockwise24Regular />} onClick={retryConnect}>
              Try again
            </Button>
          </div>
        ) : (
          <div className={s.center}>
            <Spinner label="Connecting to Katie…" />
          </div>
        )}
      </div>
    </div>
  );
}
